// components/pergola/pilastri/PilastriBase.tsx
import React from "react";
import * as THREE from "three";
import { Edges } from "@react-three/drei";
import { computeOrientation } from "../transformations";
import { FiorieraCubo, FiorieraL } from "../fioriere/fioriere";
import { useStoreDispatch, useStoreSelector } from "../../../../../../../../hooks/StoreHooks";
import { usePergolaConfigurationBuilderActions } from "../../../../../../../../store/reducers/pergola_configuration_builder";
import { Fioriera } from "../../../../../../../models/pergola_configuration";

type Sezione = { larghezza: number; altezza: number };
export const sezionePilastro: Sezione = { larghezza: 0.135, altezza: 0.135 };

type ElementCoordinateSystem = {
  org: { x: number; y: number; z: number };
  direction: [number, number, number];
};

/* ============================================= */
/* 1. Pilastro semplice (non cliccabile)         */
/* ============================================= */
type PilastroProps = {
  altezza: number;
  cs: ElementCoordinateSystem;
  material: THREE.Texture;
};

export const Pilastro: React.FC<PilastroProps> = ({ altezza, cs, material }) => {
  const rotation = computeOrientation(cs.direction) as [number, number , number];;

  return (
    <mesh position={[cs.org.x, cs.org.y, cs.org.z]} rotation={rotation}>
      <boxGeometry args={[sezionePilastro.larghezza, sezionePilastro.altezza, altezza]} />
      <meshStandardMaterial map={material} />
      <Edges color="#363636" />
    </mesh>
  );
};

/* ============================================= */
/* 2. Pilastro cliccabile + hover + fioriera     */
/* ============================================= */
type PilastroCliccabileProps = {
  id: string;
  altezza: number;
  cs: ElementCoordinateSystem;
  material: THREE.Texture;
  isSelected: boolean;
  isHovered: boolean | null;
  materialF: THREE.Texture; 
  onToggleSelect: (id: string) => void;
  onHover: (id: string | null) => void;
};


export const PilastroCliccabile: React.FC<PilastroCliccabileProps> = ({
  id,
  altezza,
  cs,
  material,
  isSelected,
  materialF,
  isHovered,
  onToggleSelect,
  onHover,
}) => {
  const { setFiorieraSingola,removeFioriera } = usePergolaConfigurationBuilderActions();
  const dispatch = useStoreDispatch();
  const pergolaConfiguration = useStoreSelector((store) => store.pergolaConfigurationBuilder);
  const rotation = computeOrientation(cs.direction) as [number, number , number];
  const handleClick = (e: any) => {
    e.stopPropagation();
    if(pergolaConfiguration.steps.find((s: any) => s.isActive)?.slug === 'fioriere'){
      if (isSelected) {
        dispatch(removeFioriera({ direzione: id }));
      } else {
        dispatch(
          setFiorieraSingola({
            direzione: id,
            tipo: Fioriera.fioriera_807
          })
        )
      }
      onToggleSelect(id);
    }
  };

  const handlePointerOver = (e: any) => {
    e.stopPropagation();
    onHover(id);
  };

  const handlePointerOut = () => onHover(null);

  return (
    <>
      {/* Pilastro principale */}
      <mesh
        position={[cs.org.x, cs.org.y, cs.org.z]}
        rotation={rotation}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <boxGeometry args={[sezionePilastro.larghezza, sezionePilastro.altezza, altezza]} />
        <meshStandardMaterial map={material} />
        <Edges color="#363636" />
      </mesh>

      {/* Effetto hover */}
      {(isHovered && pergolaConfiguration.steps.find((s: any) => s.isActive)?.slug === 'fioriere')&& (
        <mesh position={[cs.org.x, cs.org.y, cs.org.z]} rotation={rotation}>
          <boxGeometry
            args={[
              sezionePilastro.larghezza + 0.05,
              sezionePilastro.altezza + 0.05,
              altezza + 0.05,
            ]}
          />
          <meshStandardMaterial color="#ffff00" transparent opacity={0.3} depthWrite={false} />
        </mesh>
      )}
      {isSelected && <FiorieraLSelezionata cs={cs} id={id} altezzaPilastro={altezza} material={material} materialF={materialF} />}
    </>
  );
};

export const PilastroCliccabileIntermedio: React.FC<PilastroCliccabileProps> = ({
  id,
  altezza,
  cs,
  material,
  isSelected,
  isHovered,
  materialF,
  onToggleSelect,
  onHover,
}) => {
  const { setFiorieraSingola,removeFioriera } = usePergolaConfigurationBuilderActions();
  const dispatch = useStoreDispatch();
  const pergolaConfiguration = useStoreSelector((store) => store.pergolaConfigurationBuilder);
  const rotation = computeOrientation(cs.direction) as [number, number , number];
  const handleClick = (e: any) => {
    e.stopPropagation();
    if(pergolaConfiguration.steps.find((s: any) => s.isActive)?.slug === 'fioriere'){
      if (isSelected) {
        dispatch(removeFioriera({ direzione: id }));
      } else {
        dispatch(
          setFiorieraSingola({
            direzione: id,
            tipo: Fioriera.fioriera_500x500mm
          })
        )
      }
      onToggleSelect(id);
    }
  };

  const handlePointerOver = (e: any) => {
    e.stopPropagation();
    onHover(id);
  };

  const handlePointerOut = () => onHover(null);

  return (
    <>
      <mesh
        position={[cs.org.x, cs.org.y, cs.org.z]}
        rotation={rotation}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <boxGeometry args={[sezionePilastro.larghezza, sezionePilastro.altezza, altezza]} />
        <meshStandardMaterial map={material} />
        <Edges color="#363636" />
      </mesh>

      {/* Effetto hover */}
      {(isHovered && pergolaConfiguration.steps.find((s: any) => s.isActive)?.slug === 'fioriere') && (
        <mesh position={[cs.org.x, cs.org.y, cs.org.z]} rotation={rotation}>
          <boxGeometry
            args={[
              sezionePilastro.larghezza + 0.05,
              sezionePilastro.altezza + 0.05,
              altezza + 0.05,
            ]}
          />
          <meshStandardMaterial color="#ffff00" transparent opacity={0.3} depthWrite={false} />
        </mesh>
      )}
      {isSelected && <FiorieraCubo id={id} cs={cs} lunghezza={0.6} material={materialF} />}
    </>
  );
};
/* ============================================= */
/* 3. Fioriera L (solo quando il pilastro è selezionato) */
/* ============================================= */
type FiorieraLSelezionataProps = {
  cs: ElementCoordinateSystem;
  id: string;
  altezzaPilastro: number;
  material: THREE.Texture;
  materialF: THREE.Texture; 

};

const degToRad = (deg: number) => (deg * Math.PI) / 180;

export const FiorieraLSelezionata: React.FC<FiorieraLSelezionataProps> = ({
  cs,
  id,
  altezzaPilastro,
  material,
  materialF
}) => {
  const baseRotation = computeOrientation(cs.direction) as [number, number, number];

  const extraRotation: Record<string, [number, number, number]> = {
    pilastro_H1: [0, 0, degToRad(-180)],
    pilastro_H2: [0, 0, degToRad(90)],
    pilastro_H3: [0, 0, degToRad(0)],
    pilastro_H4: [0, 0, degToRad(-90)],
  };

  const additionalRot = extraRotation[id] || [0, 0, 0];
  const finalRotation: [number, number, number] = [
    baseRotation[0] + additionalRot[0],
    baseRotation[1] + additionalRot[1],
    baseRotation[2] + additionalRot[2],
  ];

  return (
    <group position={[cs.org.x, cs.org.y, 0]} rotation={finalRotation}>
      <FiorieraL
        sezione={{ larghezza: 0.45, altezza: 0.45 }}
        lunghezza={0.45}
        material={materialF}
        cs={{
          org: { x: -0.02, y: -sezionePilastro.larghezza - sezionePilastro.larghezza / 2 + 0.02, z: 0 },
          direction: [0, 0, 1],
        }}
      />
    </group>
  );
};